import { clsx } from 'clsx';
import { useMemo } from 'react';
import {
  TagGroup,
  TagGroupContext,
  TagGroupProps,
  TagList,
  TagListProps,
} from 'react-aria-components';
import { SelectionChipProps } from './SelectionChip';

import styles from './SelectionChipList.module.scss';

/** @private */
export type TagGroupContextValue = {
  selectionMode?: TagGroupProps['selectionMode'];
  size?: 'compact' | 'small' | 'medium';
  variant?: 'primary' | 'neutral' | 'brand';
};

type Item = SelectionChipProps;

export type SelectionChipListProps = {
  /** A string that labels the `SelectionChipList`. Must be used when `SelectionChipList` is not associated with a visual label using `aria-labelledby`. */
  'aria-label'?: TagGroupProps['aria-label'];
  /** The id of the element (or elements) that labels the `SelectionChipList`. Must be used when `SelectionChipList` does not receive an `aria-label`. */
  'aria-labelledby'?: TagGroupProps['aria-labelledby'];
  /** The children of the `SelectionChip`. Can be a React Node (for static lists) or a function (for dynamic lists). */
  children: TagListProps<Item>['children'];
  /** The CSS `className` for the list. A function may be provided to compute the class based on state. */
  className?: TagListProps<Item>['className'];
  /** Chip objects in the collection. Must be passed when rendering a dynamic collection. */
  items?: TagListProps<Item>['items'];
  /** The initial selected keys. For use with uncontrolled selection state. */
  defaultSelectedKeys?: TagGroupProps['defaultSelectedKeys'];
  /** Handler called when the selection changes. Must be used to update controlled selection state. */
  onSelectionChange?: TagGroupProps['onSelectionChange'];
  /** The currently selected keys. For use with controlled selection state. */
  selectedKeys?: TagGroupProps['selectedKeys'];
  /** The visual style of the chip. */
  selectionMode?: TagGroupProps['selectionMode'];
  /** The size of the `SelectionChipList`'s children. */
  size?: 'compact' | 'small' | 'medium';
  /** The inline style for the list. A function may be provided to compute the style based on state. */
  style?: TagListProps<Item>['style'];
  /** The visual style of the `SelectionChip`. */
  variant?: 'primary' | 'neutral' | 'brand';
};

/**
 * Selection Chips (also known as Suggestion or Choice Chips) are used when the user needs to make a selection among a list of options. They can also be used as not interactive to remind the user’s choice.
 *
 * They help narrow a user’s intent by presenting dynamically generated suggestions, such as possible responses.
 *
 * Write Selection Chips as nouns or short phrases, depending on context. Avoid exceeding 20 characters when possible.
 */
export const SelectionChipList = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  className,
  defaultSelectedKeys,
  items,
  onSelectionChange,
  selectedKeys,
  selectionMode,
  size = 'small',
  style,
  variant = 'primary',
}: SelectionChipListProps) => {
  const contextValue = useMemo(
    () => ({
      selectionMode: selectionMode ?? undefined,
      size,
      variant,
    }),
    [selectionMode, size, variant]
  );

  return (
    <TagGroupContext.Provider value={contextValue}>
      <TagGroup
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        data-zds
        defaultSelectedKeys={defaultSelectedKeys}
        onSelectionChange={onSelectionChange}
        selectedKeys={selectedKeys}
        selectionMode={selectionMode}
      >
        <TagList
          className={clsx(className, styles.root)}
          data-zds
          items={items}
          style={style}
        >
          {children}
        </TagList>
      </TagGroup>
    </TagGroupContext.Provider>
  );
};
